import {
  Anchor, Badge, Button, Card, Code, Grid, Group, Modal, Progress, ScrollArea, Stack, Table, Text, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconCheck, IconCircleCheck, IconFlag3, IconFlag3Filled, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import winSoundUrl from "../../assets/win.mp3";
import loseSoundUrl from "../../assets/lose.mp3";

import { api } from "../api/client";
import type { OrderSummary, LiveWindow, WindowTick } from "../api/schemas";
import { Decimal, FeedHealthPill, StatusPill } from "../components/ui";
import { FeatureIconStrip } from "../components/FeatureIconStrip";
import { useMarket, useMarketId } from "../hooks/useMarket";
import { polymarketEventUrl } from "../lib/polymarketUrl";
import { monoStyle } from "../theme";
import {
  useLiveSnapshot,
  type LiveDecisionCheck,
  type LiveDecisionPerMinute,
  type LiveDecisionRow,
  type LiveOddsTickRow,
  type LiveOrderRow,
  type LivePriceTickRow,
} from "../hooks/useLiveStream";

const FEED_OFFLINE_NOTIFICATION_ID = "live-feed-offline";
const FEED_ONLINE_NOTIFICATION_ID = "live-feed-online";

export function LivePage() {
  const {
    botStatus: wsBotStatus,
    currentWindow: wsCurrentWindow,
    priceTicks,
    oddsTicks,
    decisions: liveDecisions,
    orders: liveOrders,
    isOffline,
    socket,
  } = useLiveSnapshot();
  const [manualBusy, setManualBusy] = useState<"buy_up" | "buy_down" | "sell" | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const prevOfflineRef = useRef<boolean | null>(null);
  const marketId = useMarketId();
  // V3 multi-market: window length / minute count are derived from the
  // selected market's catalogue entry. Falls back to V2 5m=300s while
  // the catalogue is still loading.
  const { market } = useMarket();
  const windowSecondsTotal = market?.window_seconds ?? 300;
  // V3 multi-market: read per-market active config (NOT the legacy
  // global pointer). When this market has no per-market activation,
  // `active` is null and strategy-dependent UI (config row, decision
  // checks, minute candles) is hidden — the bot is only collecting
  // data, not making decisions, for this market.
  const activeCfgQ = useQuery({
    queryKey: ["market", marketId, "config", "active"],
    queryFn: () => api.markets.activeConfig(marketId),
    staleTime: 30_000,
  });
  const activeCfgRecord = activeCfgQ.data?.active ?? null;
  const hasPerMarketConfig = Boolean(activeCfgQ.data?.is_per_market);
  const isDataOnlyMarket = activeCfgQ.data != null && !hasPerMarketConfig && activeCfgRecord == null;
  // V3 multi-market: when a non-btc-5m market is selected, prefer the
  // per-market live endpoints (which only report state for that market).
  // For btc-5m we keep the legacy `/v2/live/{status,window}` shape so V2
  // clients/tests see no regression.
  const isPrimaryMarket = marketId === "btc-5m";
  // FE-1: while the WS stream is healthy (connected + not stale) the
  // REST pollers below act only as a fallback/reconciliation path, so
  // slow them from 1s to 10s. They snap back to 1s the moment the WS
  // degrades.
  const wsHealthy = socket.connected && !isOffline;
  const liveStatusQ = useQuery({
    queryKey: ["live", marketId, "status"],
    queryFn: () => (isPrimaryMarket ? api.live.status() : api.live.statusFor(marketId)),
    staleTime: 1_000,
    refetchInterval: wsHealthy ? 10_000 : 1_000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const liveWindowQ = useQuery({
    queryKey: ["live", marketId, "window"],
    queryFn: () => (isPrimaryMarket ? api.live.window() : api.live.windowFor(marketId)),
    staleTime: 1_000,
    refetchInterval: wsHealthy ? 10_000 : 1_000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const botStatus = liveStatusQ.data ?? wsBotStatus;
  const currentWindow = useMemo(
    () => preferFresherLiveWindow(wsCurrentWindow, liveWindowQ.data ?? null),
    [wsCurrentWindow, liveWindowQ.data],
  );
  const activeSlug = currentWindow?.slug ?? null;
  const resolvedOrdersQ = useQuery({
    queryKey: ["orders", "resolved", marketId, "all"],
    queryFn: () => api.orders.list({
      status: "resolved",
      mode: "all",
      limit: 200,
      market_id: marketId,
    }),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
  const openOrdersQ = useQuery({
    queryKey: ["orders", "open", "active", marketId, activeSlug, botStatus?.mode ?? "all"],
    queryFn: () => api.orders.list({
      status: "open",
      mode: botStatus?.mode ?? "all",
      limit: 200,
      market_id: marketId,
    }),
    enabled: Boolean(activeSlug),
    staleTime: 1_000,
    refetchInterval: wsHealthy ? 10_000 : 1_000,
  });
  const liveTicksQ = useQuery({
    queryKey: ["window", activeSlug, "ticks", "live-backfill"],
    queryFn: () => api.windows.ticks(activeSlug as string),
    enabled: Boolean(activeSlug),
    staleTime: 1_000,
    refetchInterval: wsHealthy ? 10_000 : 1_000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const activeOrderIdFromWindow = currentWindow?.active_order_id ?? null;
  const activeOrderDetailQ = useQuery({
    queryKey: ["orders", "detail", "active", activeOrderIdFromWindow],
    queryFn: () => api.orders.detail(activeOrderIdFromWindow as string),
    enabled: Boolean(activeOrderIdFromWindow),
    staleTime: 1_000,
    refetchInterval: wsHealthy ? 10_000 : 1_000,
  });
  const recentOutcomeOrders = useMemo(
    () => latestResolvedOrders(resolvedOrdersQ.data?.items ?? []),
    [resolvedOrdersQ.data?.items],
  );
  // Win/loss audio plays only after the 5-min market window has
  // CLOSED — never on early TP sells. We detect that by waiting for a
  // resolved order whose slug is no longer the currently active
  // window's slug (i.e. the bot has already rolled to the next
  // market). On first page load we seed `seenResolvedRef` with every
  // resolved order we see so historical orders never replay sounds.
  const SEEN_AUDIO_KEY = "live_seen_audio_ids";
  const seenResolvedRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const items = resolvedOrdersQ.data?.items ?? [];
    if (seenResolvedRef.current === null) {
      try {
        const raw = sessionStorage.getItem(SEEN_AUDIO_KEY);
        seenResolvedRef.current = raw
          ? new Set<string>(JSON.parse(raw) as string[])
          : new Set<string>();
      } catch {
        seenResolvedRef.current = new Set<string>();
      }
      // Seed with every order we already know about so we don't replay
      // sounds for resolutions that happened before this page mounted.
      for (const order of items) {
        seenResolvedRef.current.add(order.order_id);
      }
      try {
        sessionStorage.setItem(SEEN_AUDIO_KEY, JSON.stringify([...seenResolvedRef.current]));
      } catch { /* ignore */ }
      return;
    }
    for (const order of items) {
      if (seenResolvedRef.current.has(order.order_id)) continue;
      // Skip orders whose 5-min market is still the active window — we
      // want the sound to fire only AFTER the window has rolled over.
      if (activeSlug && order.slug === activeSlug) continue;
      seenResolvedRef.current.add(order.order_id);
      const pnl = order.pnl_usdc != null ? Number(order.pnl_usdc) : null;
      if (pnl == null || !Number.isFinite(pnl) || pnl === 0) continue;
      let audioEnabled = true;
      try {
        audioEnabled = localStorage.getItem("live_audio_enabled") !== '"off"';
      } catch { /* ignore */ }
      if (!audioEnabled) continue;
      try {
        const audio = new Audio(pnl > 0 ? winSoundUrl : loseSoundUrl);
        void audio.play();
      } catch { /* ignore */ }
    }
    try {
      sessionStorage.setItem(SEEN_AUDIO_KEY, JSON.stringify([...seenResolvedRef.current]));
    } catch { /* ignore */ }
  }, [resolvedOrdersQ.data?.items, activeSlug]);
  const apiActiveOrder = useMemo(() => {
    if (!activeSlug) return null;
    const fromWindow = activeOrderDetailQ.data;
    if (
      fromWindow
      && fromWindow.slug === activeSlug
      && isActiveOrderStatus(fromWindow.status)
    ) {
      return fromWindow;
    }
    return (openOrdersQ.data?.items ?? []).find((o) => o.slug === activeSlug) ?? null;
  }, [activeSlug, activeOrderDetailQ.data, openOrdersQ.data?.items]);
  const hasActiveOrder = apiActiveOrder != null;
  const activeOrderSyncPending = Boolean(activeOrderIdFromWindow)
    && (currentWindow?.secs_left == null
      ? false
      : Math.max(0, windowSecondsTotal - currentWindow.secs_left) >= Math.max(
        0,
        Math.trunc(
          asNumber(
            (activeCfgRecord?.config as Record<string, unknown> | null | undefined)?.decision_time_seconds,
            240,
          ),
        ),
      ))
    && !hasActiveOrder
    && (activeOrderDetailQ.isLoading || activeOrderDetailQ.isFetching || openOrdersQ.isFetching);
  const activeOrderLabel = useMemo(() => {
    if (apiActiveOrder) {
      return describeActiveOrder(apiActiveOrder, currentWindow?.secs_left ?? null);
    }
    if (activeOrderSyncPending) {
      return "Active order in progress...";
    }
    return "—";
  }, [apiActiveOrder, activeOrderSyncPending, currentWindow?.secs_left]);
  const mergedPriceTicks = useMemo(
    () => mergeLivePriceTicks({ wsTicks: priceTicks, historyTicks: liveTicksQ.data?.items ?? [], slug: activeSlug }),
    [priceTicks, liveTicksQ.data?.items, activeSlug],
  );
  const mergedOddsTicks = useMemo(
    () => mergeLiveOddsTicks({ wsOddsTicks: oddsTicks, historyTicks: liveTicksQ.data?.items ?? [], slug: activeSlug }),
    [oddsTicks, liveTicksQ.data?.items, activeSlug],
  );
  const cfgRecord = (activeCfgRecord?.config ?? null) as Record<string, unknown> | null;
  // V3 multi-market: the bot's candle-agreement bucket size is
  // `candle_timeframe_seconds` when non-zero, else the market's
  // `bucket_seconds`. The "Minute candles" table now mirrors that
  // bucket size 1:1, so each row corresponds to exactly one bot
  // candle. With e.g. a 15-min market on 5-min candles the table
  // shrinks from 15 rows to 3 (M1=00:00-04:59, M2=05:00-09:59,
  // M3=10:00-14:59), matching the bot's per_minute payload directly
  // and avoiding the previous "bot is 8 minutes behind" illusion.
  const botBucketSeconds = (() => {
    const fromCfg = Math.trunc(asNumber(cfgRecord?.candle_timeframe_seconds, 0));
    if (fromCfg > 0) return fromCfg;
    return Math.max(60, market?.bucket_seconds ?? 60);
  })();
  const minuteCount = Math.max(1, Math.floor(windowSecondsTotal / botBucketSeconds));
  const botCandleMinutes = Math.max(1, Math.round(botBucketSeconds / 60));
  const minuteRows = useMemo(
    () => buildLiveMinuteRows(
      mergedPriceTicks,
      currentWindow?.secs_left ?? null,
      currentWindow?.slug ?? null,
      currentWindow?.window_start ?? null,
      windowSecondsTotal,
      botBucketSeconds,
    ),
    [
      mergedPriceTicks,
      currentWindow?.secs_left,
      currentWindow?.slug,
      currentWindow?.window_start,
      windowSecondsTotal,
      botBucketSeconds,
    ],
  );
  // The dashboard table and the bot now use the same bucket size, so
  // bot.minute maps directly onto r.minute (1:1). Closed bot candles
  // and the live (in-progress) one alike land on the same row index
  // as their dashboard counterpart. Drift checks (bot.dir vs r.dir)
  // are also apples-to-apples again at every bucket size.
  const botPerMinute = useMemo(() => {
    const map = new Map<number, LiveDecisionPerMinute>();
    let directionEvaluated: "UP" | "DOWN" | null = null;
    if (!activeSlug) return { map, directionEvaluated };
    for (const dec of liveDecisions) {
      if (dec.slug !== activeSlug) continue;
      const check = (dec.checks ?? []).find((c) => c.name === "candle_agreement");
      const rows = check?.per_minute;
      if (!Array.isArray(rows) || rows.length === 0) continue;
      directionEvaluated = check?.direction_evaluated ?? null;
      for (const r of rows) map.set(r.minute, r);
      break;
    }
    return { map, directionEvaluated };
  }, [liveDecisions, activeSlug]);
  const botPerMinuteMap = botPerMinute.map;
  const botPerMinuteDir = botPerMinute.directionEvaluated;
  const minMatchingCandles = Math.max(1, Math.trunc(asNumber(cfgRecord?.min_matching_candles, 3)));
  const minOdds = asNumber(cfgRecord?.min_odds, 0.65);
  const maxOdds = asNumber(cfgRecord?.max_odds, 0.95);
  const momentumEnabled = Boolean(cfgRecord?.odds_momentum_enabled ?? false);
  const prevWindowsEnabled = Boolean(cfgRecord?.prev_windows_check_enabled ?? false);
  const kronosEnabled = Boolean(cfgRecord?.kronos_check_enabled ?? false);
  // The decision-checks table always renders 6 base columns (Time, Dir,
  // Min, Current, Max, Candles); the momentum (2 cols), prior-window and
  // Kronos gate columns are appended only when their gate is enabled in
  // the active config for the market being viewed.
  const decisionTotalCols =
    6 + (momentumEnabled ? 2 : 0) + (prevWindowsEnabled ? 1 : 0) + (kronosEnabled ? 1 : 0);
  const decisionTimeSeconds = Math.max(0, Math.trunc(asNumber(cfgRecord?.decision_time_seconds, 240)));
  const elapsedSecs = currentWindow?.secs_left == null ? null : Math.max(0, windowSecondsTotal - currentWindow.secs_left);
  const decisionWindowOpen = elapsedSecs != null && elapsedSecs >= decisionTimeSeconds;
  const decisionStartsInSecs = elapsedSecs == null ? null : Math.max(0, decisionTimeSeconds - elapsedSecs);
  const decisionCountdownLabel = formatTimeLeft(decisionStartsInSecs);
  const activeCfg = cfgRecord;
  const tradeTimeRangesEnabled = Boolean(activeCfg?.trade_time_ranges_enabled ?? false);
  const tradeTimeRangesRaw = typeof activeCfg?.trade_time_ranges === "string" ? activeCfg.trade_time_ranges : "";
  const tradeDaysEnabled = Boolean(activeCfg?.trade_days_enabled ?? false);
  const tradeDaysAllowedRaw = typeof activeCfg?.trade_days_allowed === "string" ? activeCfg.trade_days_allowed : "";
  const displayTimezone = typeof activeCfg?.display_timezone === "string" && activeCfg.display_timezone.trim()
    ? activeCfg.display_timezone.trim()
    : "UTC";
  const scheduleNow = mergedPriceTicks[0]?.ts ? new Date(mergedPriceTicks[0].ts) : new Date();
  const activeTradeRange = tradeTimeRangesEnabled
    ? activeTradeRangeLabel(scheduleNow, tradeTimeRangesRaw, displayTimezone)
    : null;
  const tradeDaysStatus = tradeDaysEnabled
    ? computeTradeDaysStatus(scheduleNow, tradeDaysAllowedRaw, displayTimezone)
    : null;
  const currentWindowDecisions = useMemo(
    () => activeSlug ? liveDecisions.filter((d) => d.slug === activeSlug) : [],
    [liveDecisions, activeSlug],
  );
  const derivedWindowDecisions = useMemo(
    () => {
      if (!decisionWindowOpen) return [] as LiveDecisionRow[];
      const cfg = cfgRecord;
      const ts = mergedOddsTicks[0]?.ts
        ?? mergedPriceTicks[0]?.ts
        ?? new Date().toISOString();
      return buildDerivedLiveDecisions(currentWindow, minuteRows, mergedOddsTicks, cfg, ts);
    },
    [
      decisionWindowOpen,
      cfgRecord,
      currentWindow,
      mergedOddsTicks,
      mergedPriceTicks,
      minuteRows,
    ],
  );
  const decisionRows = useMemo(
    () => {
      if (!decisionWindowOpen) {
        return latestDecisionRows([], null);
      }
      const bestDir = bestOddsDirection(currentWindow);
      const fromBackend = latestDecisionRows(currentWindowDecisions, bestDir);
      const fromDerived = latestDecisionRows(derivedWindowDecisions, bestDir);
      const fallbackByDir: Record<"UP" | "DOWN", LiveDecisionRow | null> = {
        UP: fromDerived.find((r) => r.dir === "UP")?.row ?? null,
        DOWN: fromDerived.find((r) => r.dir === "DOWN")?.row ?? null,
      };
      return fromBackend.map((r) => ({
        ...r,
        row: mergeDecisionWithFallback(r.row, fallbackByDir[r.dir]),
      }));
    },
    [currentWindow, decisionWindowOpen, currentWindowDecisions, derivedWindowDecisions],
  );
  const feedHealth = botStatus?.feed_health ?? mergedPriceTicks[0]?.feed_health ?? null;
  const statusState = botStatus?.state ?? (socket.connected ? "running" : undefined);
  const statusMode = botStatus?.mode;
  const upOdds = normalizedProbability(currentWindow?.odds.up.ask, currentWindow?.odds.up.bid);
  const upPercent = Math.round(upOdds * 100);
  const downPercent = Math.max(0, 100 - upPercent);
  const noBuyWindowSeconds = Math.max(0, Math.trunc(asNumber(activeCfg?.no_buy_window_seconds, 0)));
  // V3 multi-market: when the operator switches markets, the WS
  // provider drops its in-memory order ring (see useLiveStream.tsx).
  // The decision-bar markers (attempt + green executed flag) rely on
  // that ring, so they disappear until a fresh `live.order_event`
  // fires -- which never happens for an already-filled order. Hydrate
  // the marker source from the REST OrderDetail events instead so the
  // green flag survives a market round-trip.
  const restOrderEvents = useMemo<LiveOrderRow[]>(() => {
    const detail = activeOrderDetailQ.data;
    if (!detail || !detail.events?.length) return [];
    return detail.events.map((evt) => ({
      ts: evt.ts,
      slug: detail.slug,
      order_id: detail.order_id,
      phase: evt.phase,
      direction: detail.direction,
      price: evt.price,
      pnl_usdc: evt.pnl_usdc ?? null,
    }));
  }, [activeOrderDetailQ.data]);
  const timelineOrders = useMemo<LiveOrderRow[]>(() => {
    if (restOrderEvents.length === 0) return liveOrders;
    const seen = new Set(liveOrders.map((o) => `${o.order_id}:${o.phase}`));
    const merged = [...liveOrders];
    for (const o of restOrderEvents) {
      const key = `${o.order_id}:${o.phase}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(o);
    }
    return merged;
  }, [liveOrders, restOrderEvents]);
  const decisionTimeline = useMemo(
    () => buildDecisionTimeline({
      currentWindow,
      decisionTimeSeconds,
      noBuyWindowSeconds,
      orders: timelineOrders,
      windowSecondsTotal,
    }),
    [currentWindow, decisionTimeSeconds, noBuyWindowSeconds, timelineOrders, windowSecondsTotal],
  );
  const timeLeftLabel = formatTimeLeft(currentWindow?.secs_left ?? null);
  const activeCfgName = typeof cfgRecord?.config_name === "string"
    ? String(cfgRecord.config_name).trim()
    : "";
  const activeCfgLabel = activeCfgRecord
    ? (activeCfgName || activeCfgRecord.config_version_id)
    : (isDataOnlyMarket ? "— (no config)" : "—");
  // Running totals for the active config. These are attributed by the
  // authoritative `orders.config_version_id` that the trading-bot stamps
  // on every order at execution time (via `get_config_for_window`), NOT a
  // frontend time heuristic. This is the exact same source the Previous
  // Configs "Stats" column uses (`/v2/config/version-stats`), so the two
  // numbers always agree. A prior time-window filter based on
  // `applies_from_window_start` could diverge from the bot's stamping and
  // silently drop/attribute boundary orders to the wrong config.
  const activeCfgVersionId = activeCfgRecord?.config_version_id ?? null;
  const versionStatsQ = useQuery({
    queryKey: ["config", "version-stats", marketId],
    queryFn: () => api.config.versionStats({ market_id: marketId }),
    enabled: Boolean(marketId),
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
  const runningStats = useMemo(() => {
    if (!activeCfgVersionId) return null;
    return (
      versionStatsQ.data?.items.find(
        (s) => s.config_version_id === activeCfgVersionId,
      ) ?? null
    );
  }, [versionStatsQ.data, activeCfgVersionId]);
  const runningStatsLabel = useMemo(() => {
    if (!runningStats) return null;
    const pnl = Number(runningStats.pnl_usdc ?? "0");
    const pnlStr = Number.isFinite(pnl)
      ? `${pnl >= 0 ? "+" : "-"}$${Math.abs(pnl).toFixed(2)}`
      : "—";
    return `Won: ${runningStats.wins} | Total Orders: ${runningStats.total_orders} | P&L: ${pnlStr}`;
  }, [runningStats]);
  const forceBuyEnabled = Boolean(activeCfg?.force_buy_enabled ?? false);
  const canManualSell = forceBuyEnabled && apiActiveOrder?.status === "filled";

  useEffect(() => {
    const prevOffline = prevOfflineRef.current;

    if (prevOffline === null) {
      prevOfflineRef.current = isOffline;
      if (isOffline) {
        notifications.show({
          id: FEED_OFFLINE_NOTIFICATION_ID,
          color: "yellow",
          title: "Bot offline",
          message: `No live events received${socket.lastEventTs ? ` since ${new Date(socket.lastEventTs).toLocaleTimeString()}` : "."} Historical pages still work; restart trading-bot to resume the live feed.`,
          icon: <IconAlertTriangle size={16} />,
          autoClose: 5000,
        });
      }
      return;
    }

    if (isOffline && !prevOffline) {
      notifications.hide(FEED_ONLINE_NOTIFICATION_ID);
      notifications.show({
        id: FEED_OFFLINE_NOTIFICATION_ID,
        color: "yellow",
        title: "Bot offline",
        message: `No live events received${socket.lastEventTs ? ` since ${new Date(socket.lastEventTs).toLocaleTimeString()}` : "."} Historical pages still work; restart trading-bot to resume the live feed.`,
        icon: <IconAlertTriangle size={16} />,
        autoClose: 10000,
      });
    }

    if (!isOffline && prevOffline) {
      notifications.hide(FEED_OFFLINE_NOTIFICATION_ID);
      notifications.show({
        id: FEED_ONLINE_NOTIFICATION_ID,
        color: "green",
        title: "Live feed online",
        message: "Live feed is back online.",
        icon: <IconCircleCheck size={16} />,
        autoClose: 10000,
      });
    }

    prevOfflineRef.current = isOffline;
  }, [isOffline, socket.lastEventTs]);

  async function placeManualBuy(direction: "UP" | "DOWN") {
    setManualBusy(direction === "UP" ? "buy_up" : "buy_down");
    try {
      const res = await api.live.manualBuy(direction, { market_id: marketId });
      notifications.show({
        color: res.ok ? "green" : "red",
        title: "Manual order",
        message: res.message,
      });
    } catch (e) {
      notifications.show({
        color: "red",
        title: "Manual order",
        message: (e as Error)?.message ?? "manual buy failed",
      });
    } finally {
      setManualBusy(null);
    }
  }

  async function placeManualSell() {
    setManualBusy("sell");
    try {
      const res = await api.live.manualSell({ market_id: marketId });
      notifications.show({
        color: res.ok ? "green" : "red",
        title: "Manual order",
        message: res.message,
      });
    } catch (e) {
      notifications.show({
        color: "red",
        title: "Manual order",
        message: (e as Error)?.message ?? "manual sell failed",
      });
    } finally {
      setManualBusy(null);
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Live trading</Title>
        <Group gap="md">
          <FeedHealthPill value={feedHealth} />
          <StatusPill state={statusState} mode={statusMode} />
        </Group>
      </Group>

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Grid align="stretch">
              <Grid.Col span={{ base: 6 }}>
                <Card withBorder padding="sm" h="100%">
                  <Text size="xs" c="dimmed">{market?.asset ?? "BTC"}</Text>
                  <Text fw={700} size="lg" style={monoStyle}>
                    <Decimal value={currentWindow?.btc_price} digits={2} prefix="$" />
                  </Text>
                  <Text size="sm" fw={600} c="dimmed" mt={4} style={monoStyle}>
                    Time Left: {timeLeftLabel}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 6 }}>
                <Card withBorder padding="sm" h="100%">
                  <Group gap="xs" justify="space-between" wrap="nowrap">
                    <Text size="xs" c="dimmed">UP</Text>
                    <Text fw={700} size="sm" style={monoStyle} c="green">
                      <Decimal value={currentWindow?.odds.up.bid} digits={3} /> / <Decimal value={currentWindow?.odds.up.ask} digits={3} />
                    </Text>
                  </Group>
                  <Group gap="xs" justify="space-between" wrap="nowrap" mt={4}>
                    <Text size="xs" c="dimmed">DOWN</Text>
                    <Text fw={700} size="sm" style={monoStyle} c="red">
                      <Decimal value={currentWindow?.odds.down.bid} digits={3} /> / <Decimal value={currentWindow?.odds.down.ask} digits={3} />
                    </Text>
                  </Group>
                </Card>
              </Grid.Col>
            </Grid>
            {isDataOnlyMarket ? null : (
              <FeatureIconStrip
                config={cfgRecord}
                tradeTimeInWindow={tradeTimeRangesEnabled ? activeTradeRange != null : null}
                tradeDaysInWindow={tradeDaysEnabled ? (tradeDaysStatus?.allowed ?? null) : null}
              />
            )}
            <Card withBorder padding="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" fw={700}>DOWN {downPercent}%</Text>
                <Text size="xs" c="dimmed" fw={700}>UP {upPercent}%</Text>
              </Group>
              <Progress.Root size={22} radius="xl">
                <Progress.Section value={downPercent} color="loss.7">
                  <Progress.Label>DOWN</Progress.Label>
                </Progress.Section>
                <Progress.Section value={upPercent} color="profit.7">
                  <Progress.Label>UP</Progress.Label>
                </Progress.Section>
              </Progress.Root>
              {isDataOnlyMarket ? null : (
              <DecisionTimelineBar
                timeline={decisionTimeline}
                decisionWindowOpen={decisionWindowOpen}
                decisionCountdownLabel={decisionCountdownLabel}
                noBuyWindowSeconds={noBuyWindowSeconds}
              />
              )}
            </Card>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder padding="md">
            <Text size="sm" c="dimmed">Date & Time</Text>
            {currentWindow?.slug ? (
              <Anchor
                href={polymarketEventUrl({
                  slug: currentWindow.slug,
                  slugPattern: market?.slug_pattern ?? null,
                  windowStartIso: currentWindow.window_start ?? null,
                })}
                target="_blank"
                rel="noreferrer"
                fw={500}
                size="xl"
                style={monoStyle}
              >
                {formatMarketRangeWithBounds(currentWindow, market?.window_seconds ?? 300)}
              </Anchor>
            ) : (
              <Text fw={500} size="xl" style={monoStyle}>—</Text>
            )}
            <Text size="sm" c="dimmed" mt="md">Active order</Text>
            <Text size="sm" style={monoStyle}>{activeOrderLabel}</Text>
            <Text size="sm" c="dimmed" mt="md">Config</Text>
            <Anchor
              component="button"
              type="button"
              size="lg"
              fw={600}
              style={monoStyle}
              onClick={() => setConfigModalOpen(true)}
            >
              {activeCfgLabel}
            </Anchor>
            <Text size="sm" c="dimmed" mt={4}>
              {activeCfgRecord?.created_at
                ? `${new Date(activeCfgRecord.created_at).toLocaleString()}${runningStatsLabel ? ` - ${runningStatsLabel}` : ""}`
                : "—"}
            </Text>
            {isDataOnlyMarket ? (
              <Text size="xs" c="yellow" mt={6}>
                No config activated for {market?.display_name ?? marketId}. Bot is only
                collecting market data — no decisions, no orders.
              </Text>
            ) : null}
            {forceBuyEnabled ? (
              <Group mt="md" grow>
                <Button
                  color="green"
                  variant="light"
                  disabled={!currentWindow || hasActiveOrder || manualBusy !== null}
                  loading={manualBusy === "buy_up"}
                  onClick={() => void placeManualBuy("UP")}
                >
                  Buy UP
                </Button>
                <Button
                  color="red"
                  variant="light"
                  disabled={!currentWindow || hasActiveOrder || manualBusy !== null}
                  loading={manualBusy === "buy_down"}
                  onClick={() => void placeManualBuy("DOWN")}
                >
                  Buy DOWN
                </Button>
              </Group>
            ) : null}
            {canManualSell ? (
              <Button
                mt="xs"
                fullWidth
                color="orange"
                variant="light"
                disabled={!currentWindow || manualBusy !== null}
                loading={manualBusy === "sell"}
                onClick={() => void placeManualSell()}
              >
                Sell Position
              </Button>
            ) : null}
          </Card>
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder padding="md">
            <Group justify="space-between" align="center" mb="sm">
              <Title order={5}>
                {botCandleMinutes === 1
                  ? `Minute candles (M1-M${minuteCount})`
                  : `Candles · ${botCandleMinutes}m (M1-M${minuteCount})`}
              </Title>
              {botPerMinuteMap.size > 0 ? (
                <Text size="xs" c="dimmed">
                  bot view{botPerMinuteDir ? ` · ${botPerMinuteDir}` : ""}
                </Text>
              ) : null}
            </Group>
            <Table withTableBorder striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>minute</Table.Th>
                  <Table.Th>time</Table.Th>
                  <Table.Th>open</Table.Th>
                  <Table.Th>close</Table.Th>
                  <Table.Th>Δ</Table.Th>
                  <Table.Th>dir</Table.Th>
                  <Table.Th>bot Δ</Table.Th>
                  <Table.Th>bot dir</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {minuteRows.map((r) => {
                  const bot = botPerMinuteMap.get(r.minute) ?? null;
                  const botDeltaNum = bot ? Number(bot.delta) : null;
                  const botDelta = botDeltaNum != null && Number.isFinite(botDeltaNum) ? botDeltaNum : null;
                  // Dashboard rows and bot candles share the same
                  // bucket size, so dir comparison is apples-to-apples.
                  const disagrees = bot != null && r.dir != null && bot.dir !== "FLAT" && bot.dir !== r.dir;
                  return (
                    <Table.Tr key={r.minute}>
                      <Table.Td style={monoStyle}>
                        <Group gap={6} wrap="nowrap">
                          <Text component="span" size="sm" fw={600} style={monoStyle}>M{r.minute}</Text>
                          {r.isLive ? <Badge size="xs" color="green">LIVE</Badge> : null}
                          {disagrees ? (
                            <Badge size="xs" color="orange" title="bot's working.samples disagree on this minute's direction">
                              drift
                            </Badge>
                          ) : null}
                        </Group>
                      </Table.Td>
                      <Table.Td style={monoStyle}>{r.timeLabel ?? "—"}</Table.Td>
                      <Table.Td style={monoStyle}><Decimal value={r.open} digits={2} /></Table.Td>
                      <Table.Td style={monoStyle}><Decimal value={r.closed ? r.close : null} digits={2} /></Table.Td>
                      <Table.Td style={monoStyle}>
                        {r.delta == null ? "—" : `${r.delta >= 0 ? "+" : "-"}${Math.abs(r.delta).toFixed(2)}`}
                      </Table.Td>
                      <Table.Td>
                        {r.dir === "UP" ? <Text c="green" fw={600}>↑</Text> : r.dir === "DOWN" ? <Text c="red" fw={600}>↓</Text> : <Text c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td style={monoStyle}>
                        {botDelta == null
                          ? <Text c="dimmed" size="sm">—</Text>
                          : `${botDelta >= 0 ? "+" : "-"}${Math.abs(botDelta).toFixed(2)}`}
                      </Table.Td>
                      <Table.Td>
                        {bot == null
                          ? <Text c="dimmed">—</Text>
                          : bot.dir === "UP"
                            ? <Text c="green" fw={600}>↑</Text>
                            : bot.dir === "DOWN"
                              ? <Text c="red" fw={600}>↓</Text>
                              : <Text c="dimmed">—</Text>}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Card>

        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder padding="md">
            <Title order={5} mb="sm">Recent Orders</Title>
            <ScrollArea h={220} type="auto">
              <Table withTableBorder striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>market</Table.Th>
                    <Table.Th>streak</Table.Th>
                    <Table.Th>result</Table.Th>
                    <Table.Th>buy price</Table.Th>
                    <Table.Th>sell price</Table.Th>
                    <Table.Th>p/l</Table.Th>
                    <Table.Th>view</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {recentOutcomeOrders.map((o, i) => (
                    <Table.Tr key={`${o.order_id}-${o.phase}-${i}`}>
                      <Table.Td style={monoStyle}>
                        <Anchor
                          href={polymarketEventUrl({
                            slug: o.slug,
                            slugPattern: market?.slug_pattern ?? null,
                          })}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {formatMarketRange(o.slug, market?.window_seconds ?? 300)}
                        </Anchor>
                      </Table.Td>
                      <Table.Td style={monoStyle}>{o.win_streak}</Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={o.outcome === "Win" ? "green" : "red"}>{o.outcome}</Badge>
                      </Table.Td>
                      <Table.Td style={monoStyle}><Decimal value={o.buy_price_usdc} digits={2} prefix="$" /></Table.Td>
                      <Table.Td style={monoStyle}><Decimal value={o.sell_price_usdc} digits={2} prefix="$" /></Table.Td>
                      <Table.Td style={monoStyle}>{signedPnl(o.pnl_usdc)}</Table.Td>
                      <Table.Td>
                        <Anchor
                          href={`#/market-data/current?market=${encodeURIComponent(marketId)}&slug=${encodeURIComponent(o.slug)}`}
                          size="sm"
                        >
                          View
                        </Anchor>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {recentOutcomeOrders.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={7}><Text c="dimmed" size="sm">no resolved win/loss orders yet…</Text></Table.Td>
                    </Table.Tr>
                  ) : null}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
        </Grid.Col>

        {isDataOnlyMarket ? null : (
        <Grid.Col span={12}>
          <Card withBorder padding="md">
            <Title order={5} mb="sm">
              {decisionWindowOpen
                ? "Decision checks · Decision in progress..."
                : `Decision checks · starts in ${decisionCountdownLabel}`}
            </Title>
            <ScrollArea h={260} type="auto">
              <Table
                withTableBorder
                striped
                highlightOnHover
                horizontalSpacing="xs"
                verticalSpacing={6}
                style={monoStyle}
                fz="sm"
                stickyHeader
                stickyHeaderOffset={0}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Time</Table.Th>
                    <Table.Th>Dir</Table.Th>
                    <Table.Th>Min</Table.Th>
                    <Table.Th>Current</Table.Th>
                    <Table.Th>Max</Table.Th>
                    <Table.Th>Candles</Table.Th>
                    {momentumEnabled ? <Table.Th>Momentum</Table.Th> : null}
                    {momentumEnabled ? <Table.Th>Avg Momentum</Table.Th> : null}
                    {prevWindowsEnabled ? <Table.Th>Prev Windows</Table.Th> : null}
                    {kronosEnabled ? <Table.Th>Kronos</Table.Th> : null}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {decisionRows.map((d) => {
                    const currentOdds = d.row ? asFiniteNumber(d.row.favorite_odds) : null;
                    const minPassed = currentOdds == null ? null : currentOdds >= minOdds;
                    const maxPassed = currentOdds == null ? null : currentOdds <= maxOdds;
                    return (
                      <Table.Tr key={d.dir} style={d.isBestOdds ? { backgroundColor: "rgba(255, 212, 59, 0.15)" } : undefined}>
                        <Table.Td style={{ ...monoStyle, whiteSpace: "nowrap" }}>{d.row ? new Date(d.row.ts).toLocaleTimeString() : "—"}</Table.Td>
                        <Table.Td style={monoStyle}>{d.dir}</Table.Td>
                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                          <Group gap={6} wrap="nowrap">
                            <Text style={monoStyle}>{formatPct(minOdds)}</Text>
                            {passFailMark(minPassed)}
                          </Group>
                        </Table.Td>
                        <Table.Td style={monoStyle}>{d.row ? formatPct(d.row.favorite_odds) : "—"}</Table.Td>
                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                          <Group gap={6} wrap="nowrap">
                            <Text style={monoStyle}>{formatPct(maxOdds)}</Text>
                            {passFailMark(maxPassed)}
                          </Group>
                        </Table.Td>
                        <Table.Td style={{ whiteSpace: "nowrap" }}>{d.row ? candlesSummary(d.row, minMatchingCandles, decisionWindowOpen) : "— / —"}</Table.Td>
                        {momentumEnabled ? (
                          <Table.Td style={{ whiteSpace: "nowrap" }}>{d.row ? momentumSummary(d.row, decisionWindowOpen) : <Text c="dimmed" size="md">—</Text>}</Table.Td>
                        ) : null}
                        {momentumEnabled ? (
                          <Table.Td>{d.row ? avgMomentumSummary(d.row, decisionWindowOpen) : avgMomentumPlaceholder()}</Table.Td>
                        ) : null}
                        {prevWindowsEnabled ? (
                          <Table.Td style={{ whiteSpace: "nowrap" }}>{d.row ? prevWindowsSummary(d.row, decisionWindowOpen) : <Text c="dimmed" size="md">—</Text>}</Table.Td>
                        ) : null}
                        {kronosEnabled ? (
                          <Table.Td style={{ whiteSpace: "nowrap" }}>{d.row ? kronosSummary(d.row, decisionWindowOpen) : <Text c="dimmed" size="md">—</Text>}</Table.Td>
                        ) : null}
                      </Table.Tr>
                    );
                  })}
                  {decisionRows.every((d) => d.row == null) ? (
                    <Table.Tr>
                      <Table.Td colSpan={decisionTotalCols}><Text c="dimmed" size="sm">no decisions yet…</Text></Table.Td>
                    </Table.Tr>
                  ) : null}
                  {tradeTimeRangesEnabled ? (
                    <Table.Tr>
                      <Table.Td style={monoStyle}>—</Table.Td>
                      <Table.Td colSpan={decisionTotalCols - 1}>
                        <Group gap="xs" wrap="nowrap">
                          <Badge size="sm" color={activeTradeRange ? "green" : "gray"}>Time range</Badge>
                          <Text size="sm" style={monoStyle} c={activeTradeRange ? undefined : "dimmed"}>
                            {activeTradeRange
                              ? `${activeTradeRange} (${displayTimezone})`
                              : "Not in time range. No order execution."}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {tradeDaysEnabled && tradeDaysStatus ? (
                    <Table.Tr>
                      <Table.Td style={monoStyle}>—</Table.Td>
                      <Table.Td colSpan={decisionTotalCols - 1}>
                        <Group gap="xs" wrap="nowrap">
                          <Badge size="sm" color={tradeDaysStatus.allowed ? "green" : "gray"}>Trade days</Badge>
                          <Text size="sm" style={monoStyle} c={tradeDaysStatus.allowed ? undefined : "dimmed"}>
                            {tradeDaysStatus.allowed
                              ? `${tradeDaysStatus.today} allowed (${tradeDaysStatus.allowedList || "none"}, ${displayTimezone})`
                              : `${tradeDaysStatus.today} not in allowed days (${tradeDaysStatus.allowedList || "none"}, ${displayTimezone}). No order execution.`}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
        </Grid.Col>
        )}
      </Grid>

      <Modal
        opened={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        title={
          activeCfgRecord
            ? `Config ${activeCfgRecord.config_version_id} · ${new Date(activeCfgRecord.created_at).toLocaleString()}`
            : "Active config"
        }
        size="lg"
      >
        {activeCfgRecord ? (
          <ScrollArea h={420}>
            <Code block style={{ ...monoStyle, whiteSpace: "pre", fontSize: 16 }}>
              {JSON.stringify(activeCfgRecord.config, null, 2)}
            </Code>
          </ScrollArea>
        ) : (
          <Text c="dimmed" size="sm">Config metadata unavailable.</Text>
        )}
      </Modal>
    </Stack>
  );
}

function hasMomentumCheck(row: LiveDecisionRow): boolean {
  return Array.isArray(row.checks) && row.checks.some((c) => c.name.toLowerCase().includes("odds_momentum"));
}

function mergeDecisionWithFallback(
  primary: LiveDecisionRow | null,
  fallback: LiveDecisionRow | null,
): LiveDecisionRow | null {
  if (!primary) return fallback;
  if (!fallback) return primary;
  if (hasMomentumCheck(primary)) return primary;
  const mergedChecks = [
    ...(Array.isArray(primary.checks) ? primary.checks : []),
    ...(Array.isArray(fallback.checks)
      ? fallback.checks.filter((c) => c.name.toLowerCase().includes("odds_momentum"))
      : []),
  ];
  return {
    ...primary,
    matching_candles: primary.matching_candles ?? fallback.matching_candles,
    candles_evaluated: primary.candles_evaluated ?? fallback.candles_evaluated,
    checks: mergedChecks,
  };
}

function passFailMark(passed: boolean | null): JSX.Element {
  if (passed == null) return <Text c="dimmed" size="md">—</Text>;
  if (passed) {
    return <IconCheck size={20} color="var(--mantine-color-green-6)" />;
  }
  return <IconX size={20} color="var(--mantine-color-red-6)" />;
}

function formatPct(value: unknown): string {
  const n = typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : "—";
}

function asFiniteNumber(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  return Number.isFinite(n) ? n : null;
}

function momentumSummary(row: LiveDecisionRow, decisionWindowOpen: boolean): JSX.Element {
  const checks = Array.isArray(row.checks) ? row.checks : [];
  const momentum = checks.find((c) => c.name.toLowerCase().includes("odds_momentum"));
  let ratio = "0/0";
  let passed: boolean | null = null;
  let spikeDetected = false;
  if (momentum) {
    passed = momentum.passed;
    if (typeof momentum.value === "string") {
      const m = momentum.value.match(/(\d+\s*\/\s*\d+)/);
      if (m?.[1]) ratio = m[1].replace(/\s+/g, "");
    } else if (momentum.value && typeof momentum.value === "object") {
      const value = momentum.value as Record<string, unknown>;
      const increasing = asFiniteNumber(value.increasing);
      let required: number | null = null;
      if (momentum.threshold && typeof momentum.threshold === "object") {
        required = asFiniteNumber((momentum.threshold as Record<string, unknown>).required_increasing);
      } else if (typeof momentum.threshold === "string") {
        const m = momentum.threshold.match(/(\d+)(?:\s*\/\s*\d+)?/);
        required = m?.[1] ? Number(m[1]) : null;
      }
      if (increasing != null && required != null) {
        ratio = `${Math.trunc(increasing)}/${Math.trunc(required)}`;
      }
      if (value.avg_passed === false) spikeDetected = true;
    }
  }
  return (
    <Group gap={6} wrap="nowrap">
      <Text style={monoStyle}>{ratio}{spikeDetected ? " ·spike" : ""}</Text>
      {decisionWindowOpen ? passFailMark(passed) : null}
    </Group>
  );
}

function avgMomentumPlaceholder(): JSX.Element {
  return (
    <Stack gap={2} style={{ whiteSpace: "nowrap" }}>
      <Text style={monoStyle} fw={600} c="dimmed">—</Text>
      <Text style={monoStyle} size="sm" c="dimmed">—</Text>
    </Stack>
  );
}

function avgMomentumSummary(row: LiveDecisionRow, decisionWindowOpen: boolean): JSX.Element {
  const checks = Array.isArray(row.checks) ? row.checks : [];
  const momentum = checks.find((c) => c.name.toLowerCase().includes("odds_momentum"));
  if (!momentum) return avgMomentumPlaceholder();
  const value = momentum.value && typeof momentum.value === "object"
    ? (momentum.value as Record<string, unknown>)
    : null;
  const threshold = momentum.threshold && typeof momentum.threshold === "object"
    ? (momentum.threshold as Record<string, unknown>)
    : null;
  if (!value || !threshold) return avgMomentumPlaceholder();
  const avgLookback = asFiniteNumber(threshold.avg_lookback);
  const avgMaxDiff = asFiniteNumber(threshold.avg_max_diff);
  if (avgLookback == null || avgLookback <= 0 || avgMaxDiff == null || avgMaxDiff <= 0) {
    return avgMomentumPlaceholder();
  }
  const avgDiff = asFiniteNumber(value.avg_diff);
  const avgPassed = typeof value.avg_passed === "boolean" ? value.avg_passed : null;
  const avgPriorRaw = Array.isArray(value.avg_prior) ? (value.avg_prior as unknown[]) : [];
  const avgPrior = avgPriorRaw
    .map((v) => asFiniteNumber(v))
    .filter((n): n is number => n != null);
  const current = asFiniteNumber(value.current) ?? asFiniteNumber(row.favorite_odds);
  let avgVal: number | null = null;
  if (avgPrior.length > 0) {
    avgVal = avgPrior.reduce((s, v) => s + v, 0) / avgPrior.length;
  } else if (current != null && avgDiff != null) {
    avgVal = current - avgDiff;
  }
  if (avgVal == null || avgDiff == null) {
    return avgMomentumPlaceholder();
  }
  const diffLabel = `${avgDiff >= 0 ? "+" : "-"}${Math.abs(avgDiff).toFixed(3)}`;
  const diffColor = avgPassed === false
    ? "var(--mantine-color-red-7)"
    : avgPassed === true
      ? "var(--mantine-color-green-7)"
      : undefined;
  const sampleCount = avgPrior.length > 0 ? avgPrior.length : avgLookback;
  return (
    <Stack gap={2} style={{ whiteSpace: "nowrap" }}>
      <Group gap={6} wrap="nowrap" align="baseline">
        <Text style={monoStyle} fw={600}>{avgVal.toFixed(3)}</Text>
        <Text style={monoStyle} size="xs" c="dimmed">n={sampleCount}</Text>
      </Group>
      <Group gap={4} wrap="nowrap">
        <Text style={{ ...monoStyle, color: diffColor }} size="sm">{diffLabel}</Text>
        <Text style={monoStyle} size="sm" c="dimmed">/ {avgMaxDiff.toFixed(3)}</Text>
        {decisionWindowOpen ? passFailMark(avgPassed) : null}
      </Group>
    </Stack>
  );
}

function candlesSummary(row: LiveDecisionRow, minMatchingCandles: number, decisionWindowOpen: boolean): JSX.Element {
  if (row.matching_candles == null) return <Text c="dimmed" size="sm">— / —</Text>;
  const passed = row.matching_candles >= minMatchingCandles;
  return (
    <Group gap={6} wrap="nowrap">
      <Text style={monoStyle}>{`${row.matching_candles} / ${minMatchingCandles}`}</Text>
      {decisionWindowOpen ? passFailMark(passed) : null}
    </Group>
  );
}

function findDecisionCheck(row: LiveDecisionRow, name: string): LiveDecisionCheck | undefined {
  return Array.isArray(row.checks) ? row.checks.find((c) => c.name === name) : undefined;
}

function prevWindowsSummary(row: LiveDecisionRow, decisionWindowOpen: boolean): JSX.Element {
  // The `prev_windows_direction` gate is only appended once the decision
  // reaches it (odds + candles passed). When an earlier gate blocked, the
  // check is absent -> render "—" (not yet evaluated).
  const check = findDecisionCheck(row, "prev_windows_direction");
  if (!check) return <Text c="dimmed" size="md">—</Text>;
  const value = check.value && typeof check.value === "object" && !Array.isArray(check.value)
    ? (check.value as Record<string, unknown>)
    : null;
  const threshold = check.threshold && typeof check.threshold === "object"
    ? (check.threshold as Record<string, unknown>)
    : null;
  const matches = value ? asFiniteNumber(value.matches) : null;
  const count = threshold ? asFiniteNumber(threshold.count) : null;
  const minMatches = threshold ? asFiniteNumber(threshold.min_matches) : null;
  const label = matches != null && count != null
    ? `${Math.trunc(matches)}/${Math.trunc(count)}`
    : "—";
  const needLabel = minMatches != null ? `≥${Math.trunc(minMatches)}` : "";
  return (
    <Group gap={6} wrap="nowrap" title={check.detail ?? undefined}>
      <Text style={monoStyle}>{label}</Text>
      {needLabel ? <Text style={monoStyle} size="xs" c="dimmed">{needLabel}</Text> : null}
      {decisionWindowOpen ? passFailMark(check.passed) : null}
    </Group>
  );
}

function kronosSummary(row: LiveDecisionRow, decisionWindowOpen: boolean): JSX.Element {
  // The `kronos_check` gate is only appended once the decision reaches it
  // (all prior gates passed). When an earlier gate blocked, the check is
  // absent -> render "—" (not yet evaluated).
  const check = findDecisionCheck(row, "kronos_check");
  if (!check) return <Text c="dimmed" size="md">—</Text>;
  const value = check.value && typeof check.value === "object" && !Array.isArray(check.value)
    ? (check.value as Record<string, unknown>)
    : null;
  const threshold = check.threshold && typeof check.threshold === "object"
    ? (check.threshold as Record<string, unknown>)
    : null;
  const minConf = threshold ? asFiniteNumber(threshold.min_confidence) : null;
  const needLabel = minConf != null ? `≥${Math.round(minConf * 100)}%` : "";
  if (!value) {
    // Cold start: no cached forecast for this window (gate fails closed).
    return (
      <Group gap={6} wrap="nowrap" title={check.detail ?? "no cached Kronos forecast (cold start)"}>
        <Text style={monoStyle} c="dimmed">cold</Text>
        {decisionWindowOpen ? passFailMark(check.passed) : null}
      </Group>
    );
  }
  const predictedDir = typeof value.predicted_direction === "string" ? value.predicted_direction : "?";
  // `prob_picked` is the probability Kronos assigns to THIS row's own side
  // (value.direction), NOT to `predicted_direction`. Pairing the two (the old
  // "DOWN 22%") read as a contradiction; label the % with the row's side and
  // show Kronos's favoured side separately.
  const rowDir = typeof value.direction === "string" ? value.direction : predictedDir;
  const probPicked = asFiniteNumber(value.prob_picked);
  const probLabel = probPicked != null ? `${Math.round(probPicked * 100)}%` : "—";
  return (
    <Group gap={6} wrap="nowrap" title={check.detail ?? undefined}>
      <Text style={monoStyle}>{rowDir} {probLabel}</Text>
      <Text style={monoStyle} size="xs" c="dimmed">pred {predictedDir}</Text>
      {needLabel ? <Text style={monoStyle} size="xs" c="dimmed">{needLabel}</Text> : null}
      {decisionWindowOpen ? passFailMark(check.passed) : null}
    </Group>
  );
}

function latestDecisionRows(
  decisions: LiveDecisionRow[],
  bestDir: "UP" | "DOWN" | null,
): Array<{ dir: "UP" | "DOWN"; row: LiveDecisionRow | null; isBestOdds: boolean }> {
  const latest: Record<"UP" | "DOWN", LiveDecisionRow | null> = { UP: null, DOWN: null };
  for (const row of decisions) {
    if (row.direction === "UP" && latest.UP == null) latest.UP = row;
    if (row.direction === "DOWN" && latest.DOWN == null) latest.DOWN = row;
    if (latest.UP && latest.DOWN) break;
  }
  return [
    { dir: "UP", row: latest.UP, isBestOdds: bestDir === "UP" },
    { dir: "DOWN", row: latest.DOWN, isBestOdds: bestDir === "DOWN" },
  ];
}

function bestOddsDirection(window: LiveWindow | null): "UP" | "DOWN" | null {
  const up = normalizedProbability(window?.odds.up.ask, window?.odds.up.bid);
  const down = normalizedProbability(window?.odds.down.ask, window?.odds.down.bid);
  if (up === down) return null;
  return up > down ? "UP" : "DOWN";
}

function signedPnl(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n >= 0 ? "+" : "-"}$${Math.abs(n).toFixed(2)}`;
}

type OutcomeOrder = LiveOrderRow & {
  slug: string;
  outcome: "Win" | "Loss";
  win_streak: number;
  buy_price_usdc: string | null;
  sell_price_usdc: string | null;
};

function latestResolvedOrders(persisted: OrderSummary[]): OutcomeOrder[] {
  // Sort by created_at (market window placement time) rather than
  // resolved_at so that orders re-resolved later (e.g. win flipped to
  // loss after rechecking the next window) stay in their original
  // chronological position instead of jumping to the top.
  const candidateRowsDesc: LiveOrderRow[] = persisted
    .map((o) => ({
      ts: o.created_at,
      slug: o.slug,
      order_id: o.order_id,
      phase: "resolved",
      direction: o.direction,
      price: o.price,
      pnl_usdc: o.pnl_usdc,
    }))
    .sort((a, b) => {
      const diff = new Date(b.ts).getTime() - new Date(a.ts).getTime();
      if (diff !== 0) return diff;
      return b.order_id.localeCompare(a.order_id);
    });
  const candidateRowsAsc = [...candidateRowsDesc].reverse();
  const allResolved: OutcomeOrder[] = [];
  const seen = new Set<string>();
  let currentWinStreak = 0;
  for (const row of candidateRowsAsc) {
    if (seen.has(row.order_id)) continue;
    if (row.pnl_usdc == null) continue;
    const pnl = Number(row.pnl_usdc);
    if (!Number.isFinite(pnl)) continue;
    if (pnl === 0) continue;
    const source = persisted.find((p) => p.order_id === row.order_id);
    if (!source) continue;
    currentWinStreak = pnl > 0 ? currentWinStreak + 1 : 0;
    seen.add(row.order_id);
    allResolved.push({
      ...row,
      slug: source.slug,
      outcome: pnl > 0 ? "Win" : "Loss",
      win_streak: currentWinStreak,
      buy_price_usdc: source.size_usdc,
      sell_price_usdc: sumDecimalStrings(source.size_usdc, source.pnl_usdc),
    });
  }
  return allResolved.reverse().slice(0, 5);
}

function sumDecimalStrings(a: string | null, b: string | null): string | null {
  if (a == null || b == null) return null;
  const left = Number(a);
  const right = Number(b);
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  return (left + right).toFixed(2);
}

function formatMarketRange(slug: string, windowSeconds: number = 300): string {
  // V3 multi-market: the slug only carries the window's start time;
  // the window length is a property of the MarketKind. Callers must
  // pass the active market's ``window_seconds`` so non-5m markets
  // (btc-15m, btc-1h, btc-4h, eth-*) render the correct end time.
  // Defaults to 300s strictly for V2 backcompat (btc-5m).
  const windowMs = (windowSeconds > 0 ? windowSeconds : 300) * 1000;
  const epochMatch = slug.match(/-(\d{10})$/);
  if (epochMatch?.[1]) {
    const seconds = Number(epochMatch[1]);
    if (Number.isFinite(seconds)) {
      const start = new Date(seconds * 1000);
      const end = new Date(start.getTime() + windowMs);
      const dateStr = `${String(start.getMonth() + 1).padStart(2, "0")}/${String(start.getDate()).padStart(2, "0")}`;
      const startStr = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
      const endStr = `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
      return `${dateStr} ${startStr}-${endStr}`;
    }
  }
  const m = slug.match(/(\d{8})_(\d{4})$/);
  if (!m) return slug;
  const ymd = m[1]!;
  const hhmm = m[2]!;
  const year = Number(ymd.slice(0, 4));
  const month = Number(ymd.slice(4, 6)) - 1;
  const day = Number(ymd.slice(6, 8));
  const hour = Number(hhmm.slice(0, 2));
  const minute = Number(hhmm.slice(2, 4));
  const start = new Date(year, month, day, hour, minute, 0, 0);
  const end = new Date(start.getTime() + windowMs);
  const dateStr = `${String(start.getMonth() + 1).padStart(2, "0")}/${String(start.getDate()).padStart(2, "0")}`;
  const startStr = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
  const endStr = `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`;
  return `${dateStr} ${startStr}-${endStr}`;
}

function formatMarketRangeWithBounds(window: LiveWindow, windowSeconds: number = 300): string {
  const startMs = Date.parse(window.window_start);
  const endMs = Date.parse(window.window_end);
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
    const start = new Date(startMs);
    const end = new Date(endMs);
    const dateStr = start.toLocaleDateString([], { month: "2-digit", day: "2-digit" });
    const startStr = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
    const endStr = end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
    return `${dateStr} ${startStr}-${endStr}`;
  }
  return formatMarketRange(window.slug, windowSeconds);
}

function preferFresherLiveWindow(primary: LiveWindow | null, fallback: LiveWindow | null): LiveWindow | null {
  if (!primary) return fallback;
  if (!fallback) return primary;
  const primaryStart = Date.parse(primary.window_start);
  const fallbackStart = Date.parse(fallback.window_start);
  let chosen: LiveWindow = primary;
  if (Number.isFinite(primaryStart) && Number.isFinite(fallbackStart) && fallbackStart > primaryStart) {
    chosen = fallback;
  }
  // V3 multi-market: when the operator switches markets and comes
  // back, the WS provider rebuilds its in-memory `lastWindow` from
  // scratch off price/odds ticks (which carry no `active_order_id`),
  // so the WS-derived window loses the active order even though the
  // REST snapshot still has it. Backfill the missing id from whichever
  // source has it when both represent the same window. Without this
  // the "Active order" line on the dashboard goes back to "—" until a
  // fresh order_event fires.
  if (chosen.active_order_id == null && primary.slug === fallback.slug) {
    const other = chosen === primary ? fallback : primary;
    if (other.active_order_id != null) {
      return { ...chosen, active_order_id: other.active_order_id };
    }
  }
  return chosen;
}

type LiveMinuteRow = {
  minute: number;
  timeLabel: string | null;
  open: string | null;
  close: string | null;
  delta: number | null;
  dir: "UP" | "DOWN" | null;
  closed: boolean;
  isLive: boolean;
  sampleCount: number;
};

type WsPriceTick = { ts: string; slug: string; secs_left: number; btc_price: string };

function mergeLivePriceTicks(params: {
  wsTicks: LivePriceTickRow[];
  historyTicks: WindowTick[];
  slug: string | null;
}): LivePriceTickRow[] {
  const { wsTicks, historyTicks, slug } = params;
  const filteredWs = wsTicks.filter((t) => !slug || t.slug === slug);
  const mappedHistory = historyTicks.map((t) => ({
    ts: t.ts,
    slug: slug ?? "",
    secs_left: t.secs_left,
    btc_price: t.btc_price,
    mode: null,
    feed_health: t.feed_health,
  }));
  const byTs = new Map<string, LivePriceTickRow>();
  for (const row of [...filteredWs, ...mappedHistory]) {
    const prev = byTs.get(row.ts);
    if (!prev || row.mode != null) byTs.set(row.ts, row);
  }
  return [...byTs.values()].sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
}

function mergeLiveOddsTicks(params: {
  wsOddsTicks: LiveOddsTickRow[];
  historyTicks: WindowTick[];
  slug: string | null;
}): LiveOddsTickRow[] {
  const { wsOddsTicks, historyTicks, slug } = params;
  const filteredWs = wsOddsTicks.filter((t) => !slug || t.slug === slug);
  const mappedHistory = historyTicks.map((t) => ({
    ts: t.ts,
    slug: slug ?? "",
    up_bid: t.up_bid,
    up_ask: t.up_ask,
    down_bid: t.down_bid,
    down_ask: t.down_ask,
    mode: null,
    feed_health: t.feed_health,
  }));
  const byTs = new Map<string, LiveOddsTickRow>();
  for (const row of [...filteredWs, ...mappedHistory]) {
    const prev = byTs.get(row.ts);
    if (!prev || row.mode != null) byTs.set(row.ts, row);
  }
  return [...byTs.values()].sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts));
}

function buildLiveMinuteRows(
  ticks: WsPriceTick[],
  secsLeft: number | null,
  slug: string | null,
  windowStartIso: string | null = null,
  windowSecondsTotal: number = 300,
  bucketSeconds: number = 60,
): LiveMinuteRow[] {
  // V3 multi-market: the bot's candle-agreement bucket size is
  // configurable via `candle_timeframe_seconds`. The dashboard's
  // "Minute candles" table now mirrors that bucket size so each row
  // corresponds to one bot candle (M1, M2, ... where each Mn is one
  // `bucketSeconds`-wide candle). When `bucketSeconds === 60` the
  // behaviour is identical to the legacy 1-min-per-row layout.
  const bs = bucketSeconds > 0 ? bucketSeconds : 60;
  const minuteCount = Math.max(1, Math.floor(windowSecondsTotal / bs));
  // Prefer the explicit window_start from the live snapshot — that's
  // the exact value the bot uses to compute `minute_index` on its
  // working samples. Fall back to slug parsing for legacy callsites
  // where the live snapshot isn't available.
  let start: Date | null = null;
  if (windowStartIso) {
    const ms = Date.parse(windowStartIso);
    if (Number.isFinite(ms)) start = new Date(ms);
  }
  if (!start && slug) start = parseWindowStart(slug);
  const byMinute = new Map<number, WsPriceTick[]>();
  const sortedTicks = ticks
    .filter((t) => !slug || t.slug === slug)
    .slice()
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
  for (const t of sortedTicks) {
    const idx = minuteIndexForTick(t, start, windowSecondsTotal, bs);
    if (!byMinute.has(idx)) byMinute.set(idx, []);
    byMinute.get(idx)!.push(t);
  }

  return Array.from({ length: minuteCount }, (_, i) => {
    const minute = i + 1;
    const samples = byMinute.get(minute) ?? [];
    const first = samples[0] ?? null;
    const last = samples[samples.length - 1] ?? null;
    const openN = first ? Number(first.btc_price) : null;
    const lastN = last ? Number(last.btc_price) : null;
    const delta = openN != null && lastN != null && Number.isFinite(openN) && Number.isFinite(lastN)
      ? lastN - openN
      : null;
    const closed = secsLeft != null ? secsLeft <= windowSecondsTotal - minute * bs : false;
    const timeLabel = start
      ? new Date(start.getTime() + i * bs * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : null;
    return {
      minute,
      timeLabel,
      open: first?.btc_price ?? null,
      close: closed ? (last?.btc_price ?? null) : null,
      delta,
      dir: delta == null ? null : delta >= 0 ? "UP" : "DOWN",
      closed,
      isLive: !closed && samples.length > 0,
      sampleCount: samples.length,
    };
  });
}

function parseWindowStart(slug: string): Date | null {
  const epochMatch = slug.match(/-(\d{10})$/);
  if (epochMatch?.[1]) {
    const epoch = Number(epochMatch[1]);
    if (Number.isFinite(epoch)) return new Date(epoch * 1000);
  }
  const compact = slug.match(/(\d{8})_(\d{4})$/);
  if (compact?.[1] && compact?.[2]) {
    const ymd = compact[1];
    const hhmm = compact[2];
    return new Date(
      Number(ymd.slice(0, 4)),
      Number(ymd.slice(4, 6)) - 1,
      Number(ymd.slice(6, 8)),
      Number(hhmm.slice(0, 2)),
      Number(hhmm.slice(2, 4)),
      0,
      0,
    );
  }
  return null;
}

function minuteIndexForTick(
  t: WsPriceTick,
  start: Date | null,
  windowSecondsTotal: number = 300,
  bucketSeconds: number = 60,
): number {
  const bs = bucketSeconds > 0 ? bucketSeconds : 60;
  const minuteCount = Math.max(1, Math.floor(windowSecondsTotal / bs));
  // Match the trading-bot's `clock.minute_index` semantics: bucket by
  // (tick.ts - window.start) in seconds, NOT by secs_left. This keeps
  // the dashboard minute table aligned with what `_candle_agreement`
  // sees in working.samples so the two views never disagree on which
  // minute a tick belongs to.
  if (start) {
    const tickMs = new Date(t.ts).getTime();
    if (Number.isFinite(tickMs)) {
      const elapsed = Math.floor((tickMs - start.getTime()) / 1000);
      if (elapsed < 0) return 1;
      if (elapsed >= windowSecondsTotal) return minuteCount;
      return Math.min(minuteCount, Math.max(1, Math.floor(elapsed / bs) + 1));
    }
  }
  // Fallback when window.start cannot be parsed: derive elapsed from
  // secs_left as a best-effort approximation.
  const elapsedFromSecsLeft = windowSecondsTotal - t.secs_left;
  if (Number.isFinite(elapsedFromSecsLeft)) {
    return Math.min(minuteCount, Math.max(1, Math.floor(elapsedFromSecsLeft / bs) + 1));
  }
  return 1;
}

function buildDerivedLiveDecisions(
  window: LiveWindow | null,
  minuteRows: LiveMinuteRow[],
  oddsTicks: LiveOddsTickRow[],
  config: Record<string, unknown> | null,
  ts: string,
): LiveDecisionRow[] {
  const minOdds = asNumber(config?.min_odds, 0.65);
  const maxOdds = asNumber(config?.max_odds, 0.95);
  const candlesToCheck = Math.max(1, Math.trunc(asNumber(config?.candles_to_check, 4)));
  const minMatching = Math.max(1, Math.trunc(asNumber(config?.min_matching_candles, 3)));
  const allowLiveCandle = Boolean(config?.allow_live_candle ?? false);
  const liveCandleMinute = Math.max(0, Math.min(5, Math.trunc(asNumber(config?.live_candle_minute, 0))));
  const momentumEnabled = Boolean(config?.odds_momentum_enabled ?? true);
  const momentumLookback = Math.max(1, Math.trunc(asNumber(config?.odds_momentum_lookback, 8)));
  const momentumRequired = Math.max(0, Math.trunc(asNumber(config?.odds_momentum_required_increasing, 8)));
  const momentumUseUniqueOdds = Boolean(config?.odds_momentum_use_unique_odds ?? true);
  const momentumAvgLookback = Math.max(0, Math.trunc(asNumber(config?.odds_momentum_avg_lookback, 0)));
  const momentumAvgMaxDiff = Math.max(0, asNumber(config?.odds_momentum_avg_max_diff, 0.5));
  // Open-price direction feature. "open" = first candle's open (window
  // open), "current" = the live window BTC price. Market-agnostic (uses
  // the same feed the window came from).
  const openPriceGateEnabled = Boolean(config?.open_price_gate_enabled ?? false);
  const openPriceOverrideEnabled = Boolean(config?.open_price_override_enabled ?? false);
  const openPriceOverrideWithin = Math.max(0, Math.trunc(asNumber(config?.open_price_override_within_seconds, 0)));
  const openPriceCushionEnabled = Boolean(config?.open_price_cushion_enabled ?? false);
  const openPriceCushionMinBps = Math.max(0, asNumber(config?.open_price_cushion_min_bps, 2));
  const openPriceSecsLeft = Number(window?.secs_left ?? NaN);
  const sortedMinuteRows = [...minuteRows].sort((a, b) => a.minute - b.minute);
  const openPxRaw = sortedMinuteRows.length > 0 ? Number(sortedMinuteRows[0]?.open) : NaN;
  const currentPxRaw = window?.btc_price != null ? Number(window.btc_price) : NaN;
  const openPx = Number.isFinite(openPxRaw) ? openPxRaw : null;
  const currentPx = Number.isFinite(currentPxRaw) ? currentPxRaw : null;
  const closedCandles = minuteRows.filter((m) => m.closed && m.delta != null);
  const liveMinuteRow = allowLiveCandle
    ? minuteRows.find(
        (m) => m.isLive && m.sampleCount >= 2 && (liveCandleMinute === 0 || m.minute >= liveCandleMinute),
      ) ?? null
    : null;
  const qualifyingCandles = liveMinuteRow != null ? [...closedCandles, liveMinuteRow] : closedCandles;
  const candles = qualifyingCandles.slice(-candlesToCheck);

  const directions: Array<"UP" | "DOWN"> = ["UP", "DOWN"];
  const upMid = midProbability(window?.odds.up.bid, window?.odds.up.ask);
  const downMid = midProbability(window?.odds.down.bid, window?.odds.down.ask);
  const upOdds = upMid != null ? upMid : (downMid != null ? 1 - downMid : null);
  const downOdds = downMid != null ? downMid : (upMid != null ? 1 - upMid : null);
  return directions.map((dir) => {
    const odds = (dir === "UP" ? upOdds : downOdds) ?? NaN;
    const oddsValid = Number.isFinite(odds);
    const oddsPass = oddsValid && odds >= minOdds && odds <= maxOdds;
    const matching = candles.filter((c) => (dir === "UP" ? (c.delta ?? 0) >= 0 : (c.delta ?? 0) < 0)).length;
    const rawCandlesPass = matching >= minMatching;
    const openDirOk =
      openPx != null && currentPx != null &&
      (dir === "UP" ? currentPx > openPx : currentPx < openPx);
    const openOverrideActive =
      openPriceOverrideEnabled && openDirOk &&
      (openPriceOverrideWithin <= 0 ||
        (Number.isFinite(openPriceSecsLeft) && openPriceSecsLeft >= 0 && openPriceSecsLeft <= openPriceOverrideWithin));
    const candlesPass = rawCandlesPass || openOverrideActive;
    const openGatePass = !openPriceGateEnabled || openDirOk;
    const cushionBps =
      openPx != null && currentPx != null && openPx !== 0
        ? ((dir === "UP" ? currentPx - openPx : openPx - currentPx) / openPx) * 10000
        : null;
    const openCushionOk = cushionBps != null && cushionBps >= openPriceCushionMinBps;
    const openCushionGatePass = !openPriceCushionEnabled || openCushionOk;
    const momentum = deriveMomentumFromOddsHistory({
      oddsTicks,
      slug: window?.slug ?? null,
      direction: dir,
      currentOdds: oddsValid ? odds : null,
      lookback: momentumLookback,
      requiredIncreasing: momentumRequired,
      useUniqueOdds: momentumUseUniqueOdds,
      avgLookback: momentumAvgLookback,
      avgMaxDiff: momentumAvgMaxDiff,
    });
    const momentumPass = momentumEnabled ? momentum.passed : true;
    const pass = oddsPass && candlesPass && momentumPass && openGatePass && openCushionGatePass;
    const checks: LiveDecisionCheck[] = [
      {
        name: "odds_range",
        passed: oddsPass,
        value: oddsValid ? odds.toFixed(3) : null,
        threshold: `${minOdds.toFixed(2)}-${maxOdds.toFixed(2)}`,
      },
      {
        name: "candle_agreement",
        passed: candlesPass,
        value: `${matching}/${candles.length || candlesToCheck}`,
        threshold: `${minMatching}/${candlesToCheck}`,
        detail: openOverrideActive && !rawCandlesPass
          ? `${matching}/${candles.length || candlesToCheck} agree; bypassed by open-price override`
          : undefined,
      },
    ];
    if (openPriceGateEnabled) {
      checks.push({
        name: "open_price_direction",
        passed: openGatePass,
        value: openPx != null && currentPx != null ? `${currentPx} vs open ${openPx}` : null,
        threshold: dir === "UP" ? "current > open" : "current < open",
        detail: openPx == null || currentPx == null
          ? "no price sample yet for window (cold start)"
          : `current ${currentPx} ${dir === "UP" ? ">" : "<"} open ${openPx} required for ${dir}`,
      });
    }
    if (openPriceCushionEnabled) {
      checks.push({
        name: "open_price_cushion",
        passed: openCushionGatePass,
        value: cushionBps != null ? `${cushionBps.toFixed(2)} bps` : null,
        threshold: `>= ${openPriceCushionMinBps} bps`,
        detail: cushionBps == null
          ? "no price sample yet for window (cold start)"
          : `cushion ${cushionBps.toFixed(2)} bps vs min ${openPriceCushionMinBps} bps for ${dir}`,
      });
    }
    let momDetail = `${momentum.increasing} of ${momentum.observed} prior ${momentumUseUniqueOdds ? "distinct" : "recorded"} odds < current`;
    if (momentum.avgPassed === false && momentum.avgDiff != null) {
      momDetail += `; avg diff ${momentum.avgDiff.toFixed(4)} > max ${momentumAvgMaxDiff.toFixed(4)} (spike detected)`;
    }
    checks.push({
      name: "odds_momentum",
      passed: momentumEnabled ? momentum.passed : true,
      value: {
        observed: momentum.observed,
        increasing: momentum.increasing,
        avg_diff: momentum.avgDiff,
        avg_passed: momentum.avgPassed,
        raw_avg_val: momentum.rawAvgVal,
        raw_avg_n: momentum.rawAvgN,
      },
      threshold: {
        required_increasing: momentumRequired,
        lookback: momentumLookback,
        avg_lookback: momentumAvgLookback,
        avg_max_diff: momentumAvgMaxDiff,
      },
      detail: momDetail,
    });
    return {
      ts,
      slug: window?.slug ?? "",
      outcome: pass ? "place" : "blocked",
      direction: dir,
      favorite_odds: oddsValid ? odds.toFixed(3) : null,
      matching_candles: matching,
      candles_evaluated: candles.length,
      block_reason: pass ? null : !oddsPass ? "odds out of range" : !candlesPass ? "candle agreement not met" : "momentum",
      checks,
    } as LiveDecisionRow;
  });
}

type MomentumDerived = {
  passed: boolean;
  observed: number;
  increasing: number;
  avgDiff: number | null;
  avgPassed: boolean | null;
  rawAvgVal: number | null;
  rawAvgN: number;
};

function deriveMomentumFromOddsHistory(params: {
  oddsTicks: LiveOddsTickRow[];
  slug: string | null;
  direction: "UP" | "DOWN";
  currentOdds: number | null;
  lookback: number;
  requiredIncreasing: number;
  useUniqueOdds: boolean;
  avgLookback: number;
  avgMaxDiff: number;
}): MomentumDerived {
  const {
    oddsTicks,
    slug,
    direction,
    currentOdds,
    lookback,
    requiredIncreasing,
    useUniqueOdds,
    avgLookback,
    avgMaxDiff,
  } = params;
  if (currentOdds == null || !Number.isFinite(currentOdds)) {
    return {
      passed: false,
      observed: 0,
      increasing: 0,
      avgDiff: null,
      avgPassed: null,
      rawAvgVal: null,
      rawAvgN: 0,
    };
  }
  const ordered = oddsTicks
    .filter((t) => !slug || t.slug === slug)
    .slice()
    .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

  const history: number[] = [];
  const rawHistory: number[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < ordered.length; i += 1) {
    const row = ordered[i];
    if (!row) continue;
    // Mirror the trading-bot's `_implied_odds` so each tick contributes
    // the same mid value the bot recorded into `_odds_history`.
    const upMid = midProbability(row.up_bid, row.up_ask);
    const dnMid = midProbability(row.down_bid, row.down_ask);
    let chosen: number | null = direction === "UP" ? upMid : dnMid;
    if (chosen == null) {
      const other = direction === "UP" ? dnMid : upMid;
      if (other != null) chosen = 1 - other;
    }
    if (chosen == null || !Number.isFinite(chosen)) continue;
    rawHistory.push(chosen);
    const key = chosen.toFixed(4);
    if (useUniqueOdds) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    history.push(chosen);
  }
  // DEBUG: raw avg using all ticks (no uniqueness filter), last avgLookback
  const rawAvgPrior = avgLookback > 0 ? rawHistory.slice(-avgLookback) : [];
  const rawAvgVal = rawAvgPrior.length > 0
    ? rawAvgPrior.reduce((s, v) => s + v, 0) / rawAvgPrior.length
    : null;

  const priorCandidates = useUniqueOdds
    ? history.filter((v) => v !== currentOdds)
    : (() => {
      const raw = history.slice();
      for (let i = raw.length - 1; i >= 0; i -= 1) {
        if (raw[i] === currentOdds) {
          raw.splice(i, 1);
          break;
        }
      }
      return raw;
    })();
  // `lookback` is a CEILING on how many of the most-recent priors we
  // inspect, NOT a floor: early in the window we evaluate against
  // whatever priors exist so far. The gate passes as soon as
  // `requiredIncreasing` of the inspected priors are below current;
  // warm-up still fails naturally. See STRATEGY_SPEC.md §4.
  const prior = priorCandidates.slice(-lookback);
  const observed = prior.length;
  const increasing = prior.filter((v) => v < currentOdds).length;

  const primaryPassed = increasing >= requiredIncreasing;
  let avgDiff: number | null = null;
  let avgPassed: boolean | null = null;
  let passed = primaryPassed;
  if (primaryPassed && avgLookback > 0 && avgMaxDiff > 0) {
    const avgPriorCandidates = priorCandidates.slice(-avgLookback);
    if (avgPriorCandidates.length > 0) {
      const avgVal = avgPriorCandidates.reduce((s, v) => s + v, 0) / avgPriorCandidates.length;
      avgDiff = currentOdds - avgVal;
      avgPassed = avgDiff <= avgMaxDiff;
      if (!avgPassed) passed = false;
    }
  }
  return {
    passed,
    observed,
    increasing,
    avgDiff,
    avgPassed,
    rawAvgVal,
    rawAvgN: rawAvgPrior.length,
  };
}

function asNumber(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/**
 * Bid/ask midpoint matching the trading-bot's `_implied_odds`. Returns
 * `null` when either side of the book is missing so callers can apply
 * the `1 - other_mid` fallback consistently with the bot.
 */
function midProbability(bid: string | null | undefined, ask: string | null | undefined): number | null {
  if (bid == null || ask == null) return null;
  const b = Number(bid);
  const a = Number(ask);
  if (!Number.isFinite(b) || !Number.isFinite(a)) return null;
  return (b + a) / 2;
}

function normalizedProbability(primary: string | null | undefined, fallback: string | null | undefined): number {
  const first = Number(primary ?? NaN);
  const second = Number(fallback ?? NaN);
  const chosen = Number.isFinite(first) ? first : Number.isFinite(second) ? second : 0.5;
  return Math.max(0, Math.min(1, chosen));
}

const TRADE_DAY_TOKENS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
const TRADE_DAY_ALIASES: Record<string, string> = {
  MON: "MON", MONDAY: "MON",
  TUE: "TUE", TUES: "TUE", TUESDAY: "TUE",
  WED: "WED", WEDS: "WED", WEDNESDAY: "WED",
  THU: "THU", THUR: "THU", THURS: "THU", THURSDAY: "THU",
  FRI: "FRI", FRIDAY: "FRI",
  SAT: "SAT", SATURDAY: "SAT",
  SUN: "SUN", SUNDAY: "SUN",
};

function computeTradeDaysStatus(
  now: Date,
  allowedRaw: string,
  timezoneName: string,
): { allowed: boolean; today: string; allowedList: string } | null {
  const parsed = new Set<string>();
  for (const piece of (allowedRaw ?? "").split(",")) {
    const token = piece.trim().toUpperCase();
    if (!token) continue;
    const norm = TRADE_DAY_ALIASES[token];
    if (norm) parsed.add(norm);
  }
  let today: string;
  try {
    today = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneName || "UTC",
      weekday: "short",
    }).format(now).toUpperCase();
  } catch {
    today = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short" })
      .format(now).toUpperCase();
  }
  const allowedList = TRADE_DAY_TOKENS.filter((t) => parsed.has(t)).join(",");
  const allowed = parsed.size > 0 && parsed.has(today);
  return { allowed, today, allowedList };
}

function activeTradeRangeLabel(now: Date, rangesRaw: string, timezoneName: string): string | null {
  const ranges = parseTradeTimeRanges(rangesRaw);
  if (ranges.length === 0) return null;
  const minuteOfDay = localMinuteOfDay(now, timezoneName);
  if (minuteOfDay == null) return null;
  for (const r of ranges) {
    if (r.startMinute <= r.endMinute) {
      if (minuteOfDay >= r.startMinute && minuteOfDay <= r.endMinute) {
        return r.label;
      }
      continue;
    }
    if (minuteOfDay >= r.startMinute || minuteOfDay <= r.endMinute) {
      return r.label;
    }
  }
  return null;
}

function parseTradeTimeRanges(raw: string): Array<{ startMinute: number; endMinute: number; label: string }> {
  if (!raw.trim()) return [];
  const out: Array<{ startMinute: number; endMinute: number; label: string }> = [];
  for (const piece of raw.split(",")) {
    const token = piece.trim();
    if (!token) continue;
    const pair = token.split("-");
    if (pair.length !== 2) continue;
    const start = parseHhMm(pair[0] ?? "");
    const end = parseHhMm(pair[1] ?? "");
    if (start == null || end == null) continue;
    out.push({ startMinute: start, endMinute: end, label: `${formatHhMm(start)}-${formatHhMm(end)}` });
  }
  return out;
}

function localMinuteOfDay(now: Date, timezoneName: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneName,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const hh = Number(parts.find((p) => p.type === "hour")?.value ?? NaN);
    const mm = Number(parts.find((p) => p.type === "minute")?.value ?? NaN);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return (hh * 60) + mm;
  } catch {
    return null;
  }
}

function parseHhMm(v: string): number | null {
  const txt = v.trim();
  if (!txt.includes(":")) return null;
  const [hhRaw, mmRaw] = txt.split(":", 2);
  const hh = Number(hhRaw);
  const mm = Number(mmRaw);
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return (hh * 60) + mm;
}

function formatHhMm(minuteOfDay: number): string {
  const hh = Math.floor(minuteOfDay / 60);
  const mm = minuteOfDay % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

type TimelineMarker = {
  key: string;
  position: number; // 0..1 along the decision-portion bar
  kind: "attempt" | "executed";
  direction: string;
  ts: string;
  phase: string;
};

type DecisionTimeline = {
  decisionTotalSecs: number;
  elapsedPercent: number; // 0..100 of the decision portion
  noBuyPercent: number;   // 0..100 of the decision portion (right-aligned band)
  markers: TimelineMarker[];
};

function buildDecisionTimeline(params: {
  currentWindow: LiveWindow | null;
  decisionTimeSeconds: number;
  noBuyWindowSeconds: number;
  orders: LiveOrderRow[];
  windowSecondsTotal: number;
}): DecisionTimeline {
  const { currentWindow, decisionTimeSeconds, noBuyWindowSeconds, orders, windowSecondsTotal } = params;
  const decisionTotalSecs = Math.max(0, windowSecondsTotal - decisionTimeSeconds);
  if (!currentWindow || decisionTotalSecs <= 0) {
    return { decisionTotalSecs, elapsedPercent: 0, noBuyPercent: 0, markers: [] };
  }
  const secsLeft = currentWindow.secs_left ?? null;
  const elapsedSecs = secsLeft == null ? 0 : Math.max(0, windowSecondsTotal - secsLeft);
  const elapsedInDecision = Math.max(0, Math.min(decisionTotalSecs, elapsedSecs - decisionTimeSeconds));
  const elapsedPercent = (elapsedInDecision / decisionTotalSecs) * 100;
  const noBuyPercent = Math.min(100, (Math.min(noBuyWindowSeconds, decisionTotalSecs) / decisionTotalSecs) * 100);

  const wsStart = Date.parse(currentWindow.window_start);
  const wsEnd = Date.parse(currentWindow.window_end);
  const decisionStartMs = wsStart + decisionTimeSeconds * 1000;
  const decisionSpanMs = decisionTotalSecs * 1000;

  const ATTEMPT_PHASES = new Set(["intent", "submitted", "accepted"]);
  const FILL_PHASES = new Set(["filled", "partially_filled"]);

  const byOrder = new Map<string, LiveOrderRow[]>();
  for (const o of orders) {
    if (o.slug !== currentWindow.slug) continue;
    const t = Date.parse(o.ts);
    if (!Number.isFinite(t)) continue;
    if (t < wsStart || t >= wsEnd) continue;
    const arr = byOrder.get(o.order_id) ?? [];
    arr.push(o);
    byOrder.set(o.order_id, arr);
  }

  const markers: TimelineMarker[] = [];
  for (const [order_id, events] of byOrder) {
    const sorted = [...events].sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
    const attempt = sorted.find((e) => ATTEMPT_PHASES.has(e.phase));
    const fill = sorted.find((e) => FILL_PHASES.has(e.phase));
    const pushMarker = (row: LiveOrderRow, kind: "attempt" | "executed") => {
      const t = Date.parse(row.ts);
      const offset = t - decisionStartMs;
      if (offset < 0 || offset > decisionSpanMs) return;
      markers.push({
        key: `${order_id}:${kind}`,
        position: Math.max(0, Math.min(1, offset / decisionSpanMs)),
        kind,
        direction: row.direction,
        ts: row.ts,
        phase: row.phase,
      });
    };
    if (attempt) pushMarker(attempt, "attempt");
    if (fill) pushMarker(fill, "executed");
  }

  return { decisionTotalSecs, elapsedPercent, noBuyPercent, markers };
}

const DECISION_TIMELINE_KEYFRAMES = `
@keyframes decisionTimelineBob {
  0%   { transform: translate(-50%, 0)    rotate(-4deg); }
  50%  { transform: translate(-50%, -3px) rotate(4deg); }
  100% { transform: translate(-50%, 0)    rotate(-4deg); }
}
@keyframes decisionTimelinePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 139, 230, 0.55); }
  50%      { box-shadow: 0 0 0 6px rgba(34, 139, 230, 0); }
}
@keyframes decisionTimelineSheen {
  0%   { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}
`;

function DecisionTimelineBar(props: {
  timeline: DecisionTimeline;
  decisionWindowOpen: boolean;
  decisionCountdownLabel: string;
  noBuyWindowSeconds: number;
}): JSX.Element {
  const { timeline, decisionWindowOpen, decisionCountdownLabel, noBuyWindowSeconds } = props;
  const { elapsedPercent, noBuyPercent, markers } = timeline;
  const showRunner = decisionWindowOpen && elapsedPercent > 0 && elapsedPercent < 100;
  const runnerLeft = Math.max(0, Math.min(100, elapsedPercent));
  return (
    <div style={{ marginTop: 18 }}>
      <style>{DECISION_TIMELINE_KEYFRAMES}</style>
      <Group justify="space-between" mb={6} align="center">
        <Group gap={6} align="center">
          <Text component="span" size="sm" aria-hidden>🏁</Text>
          <Text size="xs" fw={800} c={decisionWindowOpen ? "blue" : "dimmed"} style={{ letterSpacing: 0.5 }}>
            {decisionWindowOpen
              ? `RACE ON · decision window open`
              : `STARTING IN ${decisionCountdownLabel}`}
          </Text>
        </Group>
        {noBuyWindowSeconds > 0 ? (
          <Group gap={6} align="center">
            <Text component="span" size="sm" aria-hidden>🚫</Text>
            <Text size="xs" fw={800} c="red" style={{ letterSpacing: 0.5 }}>
              NO-BUY ZONE · LAST {noBuyWindowSeconds}s
            </Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed" fw={700}>No-buy: off</Text>
        )}
      </Group>

      {/* Track */}
      <div
        style={{
          position: "relative",
          height: 30,
          borderRadius: 999,
          background:
            "repeating-linear-gradient(90deg, light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5)) 0 18px, light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-6)) 18px 36px)",
          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.18)",
          overflow: "visible",
          border: "1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))",
        }}
      >
        {/* Progress fill with animated sheen */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${elapsedPercent}%`,
            backgroundImage:
              "linear-gradient(90deg, var(--mantine-color-blue-4) 0%, var(--mantine-color-blue-6) 50%, var(--mantine-color-blue-4) 100%)",
            backgroundSize: "200% 100%",
            animation: showRunner ? "decisionTimelineSheen 2.5s linear infinite" : undefined,
            borderRadius: 999,
            transition: "width 0.6s linear",
            boxShadow: "inset 0 -2px 4px rgba(0,0,0,0.15), 0 1px 2px rgba(34,139,230,0.35)",
          }}
        />

        {/* No-buy danger zone overlay */}
        {noBuyPercent > 0 ? (
          <div
            title={`No-buy window · last ${noBuyWindowSeconds}s of the window`}
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: `${noBuyPercent}%`,
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(250,82,82,0.85) 0 8px, rgba(255,168,168,0.85) 8px 16px)",
              borderRadius: 999,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderLeft: "2px dashed var(--mantine-color-red-7)",
            }}
          >
            {noBuyPercent > 14 ? (
              <Text
                size="xs"
                fw={900}
                c="white"
                style={{ letterSpacing: 0.8, textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
              >
                NO BUY
              </Text>
            ) : null}
          </div>
        ) : null}

        {/* Running indicator riding the leading edge of progress */}
        {showRunner ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: `${runnerLeft}%`,
              top: -6,
              transform: "translate(-50%, 0)",
              fontSize: 24,
              lineHeight: 1,
              animation: "decisionTimelineBob 0.6s ease-in-out infinite",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.35))",
            }}
          >
            🏃‍♂️
          </div>
        ) : null}

        {/* Start tick */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: -10,
            bottom: -10,
            width: 4,
            borderRadius: 2,
            backgroundImage:
              "repeating-linear-gradient(0deg, #111 0 4px, #fff 4px 8px)",
            border: "1px solid var(--mantine-color-gray-7)",
          }}
        />
        {/* Finish tick */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            right: 0,
            top: -10,
            bottom: -10,
            width: 4,
            borderRadius: 2,
            backgroundImage:
              "repeating-linear-gradient(0deg, #111 0 4px, #fff 4px 8px)",
            border: "1px solid var(--mantine-color-gray-7)",
          }}
        />

        {/* Order markers */}
        {markers.map((m) => (
          <div
            key={m.key}
            title={`${m.kind === "executed" ? "Order filled" : "Order attempt"} · ${m.direction} · ${new Date(m.ts).toLocaleTimeString()}`}
            style={{
              position: "absolute",
              left: `${m.position * 100}%`,
              transform: "translate(-50%, 0)",
              top: -22,
              width: 28,
              height: 50,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              filter: m.kind === "executed"
                ? "drop-shadow(0 0 6px rgba(64, 192, 87, 0.7))"
                : "drop-shadow(0 1px 2px rgba(0,0,0,0.4))",
              pointerEvents: "auto",
              zIndex: 2,
            }}
          >
            {m.kind === "executed" ? (
              <IconFlag3Filled size={26} color="var(--mantine-color-green-6)" stroke={1.5} />
            ) : (
              <IconFlag3 size={26} color="#fff" stroke={2.4} />
            )}
            {/* Pole shadow connecting flag to bar */}
            <div
              style={{
                width: 2,
                height: 8,
                backgroundColor: m.kind === "executed"
                  ? "var(--mantine-color-green-7)"
                  : "var(--mantine-color-gray-6)",
                marginTop: -4,
                borderRadius: 1,
              }}
            />
          </div>
        ))}
      </div>

      {/* Bottom labels */}
      <Group justify="space-between" mt={6} align="center">
        <Group gap={4} align="center">
          <Text component="span" size="xs" aria-hidden>🚦</Text>
          <Text size="xs" c="dimmed" style={monoStyle}>
            START
          </Text>
        </Group>
        <Group gap={4} align="center">
          <Text size="xs" c="dimmed" style={monoStyle}>
            FINISH
          </Text>
          <Text component="span" size="xs" aria-hidden>🏁</Text>
        </Group>
      </Group>
    </div>
  );
}

function formatTimeLeft(secsLeft: number | null): string {
  if (secsLeft == null || !Number.isFinite(secsLeft)) return "—";
  const total = Math.max(0, Math.floor(secsLeft));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
}

function describeActiveOrder(order: OrderSummary | null, secsLeft: number | null): string {
  if (!order) return "—";
  const direction = order.direction === "UP" ? "UP" : "DOWN";
  const pct = formatOrderPercent(order.price);
  // If the position has already been sold early (TP / stop-loss /
  // manual) keep showing the entry but indicate the current realised
  // P&L so the operator knows the market is awaiting final
  // resolution before the win/loss sound fires.
  if (order.sold_early) {
    const pnlNum = order.pnl_usdc != null ? Number(order.pnl_usdc) : null;
    const pnlLabel = (pnlNum != null && Number.isFinite(pnlNum))
      ? ` (sold ${pnlNum >= 0 ? "+" : "-"}$${Math.abs(pnlNum).toFixed(2)})`
      : " (sold)";
    return `Bought ${direction} @ ${pct}${pnlLabel}`;
  }
  const left = formatOrderSecsLeft(secsLeft);
  return `Bought ${direction} @ ${pct} with ${left} left`;
}

function isActiveOrderStatus(status: OrderSummary["status"]): boolean {
  // "resolved" is included so that early-sold orders keep occupying
  // the active-order slot on the dashboard until the 5-min window
  // itself rolls over (which clears active_order_id upstream).
  return status === "open" || status === "filled" || status === "resolved";
}

function formatOrderPercent(price: string | null): string {
  if (price == null) return "—";
  const n = Number(price);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function formatOrderSecsLeft(secsLeft: number | null): string {
  if (secsLeft == null || !Number.isFinite(secsLeft)) return "—";
  return `${Math.max(0, Math.floor(secsLeft))}s`;
}
